import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { useFormikContext } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useGetInvoiceSettingsQuery,
  useGetInvoiceCustomerQuery,
  useUpdateInvoiceCustomerMutation,
  useCreateOrUpdateInvoiceSettingsMutation,
} from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { Invoice } from "types/Models/invoice";
import { isValidEmail } from "util/isValidEmail";
import {
  INVOICE_CC_SCOPE_THIS_INVOICE,
  INVOICE_CC_SCOPE_CUSTOMER,
  INVOICE_CC_SCOPE_ENTITY,
  INVOICE_CC_SCOPE_VALUES,
  InvoiceCcScope,
} from "constants/invoiceCcScope";
import { GridLayout } from "./GridLayout";
import { SendInvoiceForm } from "./SendInvoiceModal";

type CcSelectOption = {
  value: string;
  label: string;
};

const isCcScopeValue = (value: string): value is InvoiceCcScope =>
  (INVOICE_CC_SCOPE_VALUES as string[]).includes(value);

export const InvoiceCcField = ({ invoice }: { invoice: Invoice }) => {
  const { values, setFieldValue } = useFormikContext<SendInvoiceForm>();
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const [ccInputValue, setCcInputValue] = useState("");

  const [updateInvoiceCustomer, { isLoading: isUpdatingCustomer }] =
    useUpdateInvoiceCustomerMutation();
  const [createOrUpdateInvoiceSettings] =
    useCreateOrUpdateInvoiceSettingsMutation();

  const { data: invoiceSetting } = useGetInvoiceSettingsQuery(
    {
      groupId: group.uuid,
      entityId,
    },
    {
      skip: !group.uuid || !entityId,
    }
  );

  const { data: customerData } = useGetInvoiceCustomerQuery(
    {
      groupId: group.uuid,
      entityId: entityId!,
      customerId: invoice?.customer?.customer_id!,
    },
    {
      skip: !group.uuid || !entityId || !invoice?.customer?.customer_id,
    }
  );

  const updateCCRecipientEmails = async (emails: string[]) => {
    if (
      !group?.uuid ||
      !entityId ||
      !invoice?.customer?.customer_id ||
      !invoice?.customer
    ) {
      return;
    }

    try {
      await updateInvoiceCustomer({
        groupId: group.uuid,
        entityId: entityId,
        customerId: invoice.customer.customer_id,
        payload: {
          company_name: invoice.customer.company_name,
          email: invoice.customer.email || "",
          phone: invoice.customer.phone || "",
          is_reminders_enabled: invoice.customer.is_reminders_enabled ?? false,
          currency: invoice.customer.currency,
          cc_recipient_emails: emails || [],
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const internalTeamCcOptions: CcSelectOption[] = (
    invoiceSetting?.cc_recipient_emails || []
  ).map((email) => ({ value: email, label: email }));

  const customerTeamCcOptions: CcSelectOption[] = (
    customerData?.cc_recipient_emails || []
  ).map((email) => ({ value: email, label: email }));

  const ccGroupedOptions = [
    { label: "Internal Team", options: internalTeamCcOptions },
    { label: "Customer's Team", options: customerTeamCcOptions },
  ];

  const selectedCcEmailOptions: CcSelectOption[] = (values.cc_emails || []).map(
    (email: string) => ({
      value: email,
      label: email,
    })
  );

  const trimmedCcInput = ccInputValue.trim();
  const knownCcEmails = [
    ...internalTeamCcOptions,
    ...customerTeamCcOptions,
  ].map((option) => option.value);
  const isNewCcEmail =
    trimmedCcInput.length > 0 &&
    isValidEmail(trimmedCcInput) &&
    !knownCcEmails.includes(trimmedCcInput) &&
    !(values.cc_emails || []).includes(trimmedCcInput);

  const customerDisplayName =
    customerData?.company_name || invoice?.customer?.company_name || "customer";

  const ccScopeOptions: { label: string; options: CcSelectOption[] }[] = [
    {
      label: `Add ${trimmedCcInput}`,
      options: [
        {
          value: INVOICE_CC_SCOPE_THIS_INVOICE,
          label: "For this invoice only",
        },
        {
          value: INVOICE_CC_SCOPE_CUSTOMER,
          label: `For all future invoices, for ${customerDisplayName}`,
        },
        {
          value: INVOICE_CC_SCOPE_ENTITY,
          label: "For all future invoices, for all customers",
        },
      ],
    },
  ];

  const ccOptionsToShow = isNewCcEmail ? ccScopeOptions : ccGroupedOptions;

  const handleAddNewCcEmail = async (scope: InvoiceCcScope) => {
    const email = trimmedCcInput;
    if (!email) {
      return;
    }

    setFieldValue("cc_emails", [...(values.cc_emails || []), email]);
    setCcInputValue("");

    if (scope === INVOICE_CC_SCOPE_CUSTOMER) {
      updateCCRecipientEmails([
        ...(customerData?.cc_recipient_emails || []),
        email,
      ]);
      return;
    }

    if (scope === INVOICE_CC_SCOPE_ENTITY) {
      try {
        await createOrUpdateInvoiceSettings({
          groupId: group.uuid,
          entityId: entityId!,
          payload: {
            cc_recipient_emails: [
              ...(invoiceSetting?.cc_recipient_emails || []),
              email,
            ],
          },
        }).unwrap();
      } catch (error) {
        alertToast(
          { message: (error as BackendError).data?.error?.message },
          error as Error
        );
      }
    }
  };

  return (
    <GridLayout>
      <Label htmlFor="cc_emails">CC</Label>
      <Combobox
        name="cc_emails"
        size="small"
        isMulti={!isNewCcEmail}
        placeholder="Add email address"
        options={ccOptionsToShow}
        value={isNewCcEmail ? null : selectedCcEmailOptions}
        inputValue={ccInputValue}
        isLoading={isUpdatingCustomer}
        filterOption={(option, rawInput) => {
          if (isCcScopeValue(option.value)) {
            return true;
          }
          return option.label
            .toLowerCase()
            .includes(rawInput.trim().toLowerCase());
        }}
        onInputChange={(newValue, meta) => {
          if (meta.action === "input-change") {
            setCcInputValue(newValue);
          }
        }}
        onChange={(selectedOption) => {
          if (isNewCcEmail) {
            const clickedValue = (selectedOption as CcSelectOption | null)
              ?.value;
            if (clickedValue && isCcScopeValue(clickedValue)) {
              handleAddNewCcEmail(clickedValue);
            }
            return;
          }

          const emails = selectedOption
            ? (selectedOption as CcSelectOption[]).map((opt) => opt.value)
            : [];
          setFieldValue("cc_emails", emails);
        }}
      />
      <div />
      <p className="t-m-0 t-text-body-sm t-text-neutral-40">
        A new email can be added for this invoice only, saved for this customer,
        or saved for all customers.
      </p>
    </GridLayout>
  );
};
